import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Copy } from "lucide-react";

export const AddClientDialog = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    contact_person: "",
    compuoffice_id: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () => {
    setForm({ name: "", email: "", phone: "", address: "", contact_person: "", compuoffice_id: "" });
    setCreated(null);
  };

  const handleClose = (open) => {
    if (!open) reset();
    onOpenChange(open);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone || null,
        address: form.address || null,
        email: form.email.trim() ? form.email.trim() : null,
        contact_person: form.contact_person.trim() || null,
        compuoffice_id: form.compuoffice_id.trim() || null,
      };
      const { data } = await api.post("/admin/clients", payload);
      setCreated(data);
      toast.success(`Client ${data.name} added — ID ${data.client_code}`);
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const copyCode = () => {
    if (!created) return;
    navigator.clipboard.writeText(created.client_code);
    toast.success("Client ID copied");
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" data-testid="add-client-dialog">
        {!created ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display font-medium text-2xl">
                Add Client
              </DialogTitle>
              <DialogDescription className="text-slate-500">
                A unique Client ID will be generated. Share it with your client so they can
                sign up with any email of their choice and access their invoices.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="add-client-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-tag">Name</Label>
                  <Input
                    required
                    value={form.name}
                    onChange={setField("name")}
                    data-testid="new-client-name"
                    className="rounded-md border-slate-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-tag">Phone</Label>
                  <Input
                    value={form.phone}
                    onChange={setField("phone")}
                    data-testid="new-client-phone"
                    className="rounded-md border-slate-300"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="label-tag">
                  Contact email <span className="text-slate-400 normal-case tracking-normal font-normal">(optional)</span>
                </Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={setField("email")}
                  data-testid="new-client-email"
                  className="rounded-md border-slate-300"
                  placeholder="For your records / invoice email delivery"
                />
                <p className="text-xs text-slate-400">
                  Not required for client sign-up. Your client can register with any email using
                  their Client ID.
                </p>
              </div>
              <div className="space-y-2">
                <Label className="label-tag">Address</Label>
                <Textarea
                  rows={2}
                  value={form.address}
                  onChange={setField("address")}
                  data-testid="new-client-address"
                  className="rounded-md border-slate-300 resize-none"
                />
              </div>

              <div className="pt-2 border-t border-slate-100">
                <p className="label-tag mb-3 text-slate-400">Optional details</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="label-tag">Contact person</Label>
                    <Input
                      value={form.contact_person}
                      onChange={setField("contact_person")}
                      data-testid="new-client-contact-person"
                      className="rounded-md border-slate-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-tag">CompuOffice ID</Label>
                    <Input
                      value={form.compuoffice_id}
                      onChange={setField("compuoffice_id")}
                      data-testid="new-client-compuoffice-id"
                      className="rounded-md border-slate-300 font-mono"
                    />
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleClose(false)}
                  className="border-slate-300"
                  data-testid="cancel-add-client"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="submit-add-client"
                >
                  {submitting ? "Creating…" : "Add Client"}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <div data-testid="client-created-success">
            <DialogHeader>
              <DialogTitle className="font-display font-medium text-2xl flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                Client Added
              </DialogTitle>
              <DialogDescription className="text-slate-500">
                Share this Client ID with <strong>{created.name}</strong> so they can register.
              </DialogDescription>
            </DialogHeader>

            <div className="my-6 border-2 border-dashed border-slate-300 bg-slate-50 p-6 text-center rounded-md">
              <p className="label-tag mb-2">Client ID</p>
              <p
                className="font-mono text-3xl font-semibold tracking-wider text-slate-900"
                data-testid="created-client-code"
              >
                {created.client_code}
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3 border-slate-300"
                onClick={copyCode}
                data-testid="copy-client-code-btn"
              >
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
              </Button>
            </div>

            <div className="text-sm text-slate-600 space-y-1 border-t border-slate-200 pt-4">
              <p><strong>Name:</strong> {created.name}</p>
              {created.email && <p><strong>Email:</strong> {created.email}</p>}
              {created.phone && <p><strong>Phone:</strong> {created.phone}</p>}
            </div>

            <DialogFooter className="mt-6">
              <Button
                onClick={() => handleClose(false)}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="close-client-success"
              >
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
